import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SherwinWilliamsColor } from '@/types/firestore';

interface ColorSwatchProps {
  color: SherwinWilliamsColor;
  isSelected?: boolean;
  onClick?: () => void;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export function ColorSwatch({
  color,
  isSelected = false,
  onClick,
  size = 'md',
  showLabel = true,
}: ColorSwatchProps) {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
  };

  // Determine if we need light or dark check mark based on LRV
  const needsLightCheck = color.lrv < 50;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Select ${color.name} (${color.code})`}
      aria-pressed={isSelected}
      className={cn(
        'group flex flex-col items-center gap-1 transition-all',
        onClick && 'cursor-pointer hover:scale-105'
      )}
    >
      <div
        className={cn(
          'relative rounded-lg shadow-sm ring-2 ring-offset-2 transition-all',
          sizeClasses[size],
          isSelected
            ? 'ring-primary ring-offset-background'
            : 'ring-transparent hover:ring-muted-foreground/30'
        )}
        style={{ backgroundColor: color.hex }}
        title={`${color.name} (${color.code})`}
        aria-hidden="true"
      >
        {isSelected && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Check
              className={cn(
                'w-5 h-5',
                needsLightCheck ? 'text-white' : 'text-gray-800'
              )}
              strokeWidth={3}
            />
          </div>
        )}
      </div>
      {showLabel && (
        <div className="text-center">
          <p className={cn(
            'font-medium truncate max-w-[80px]',
            size === 'sm' ? 'text-[10px]' : 'text-xs'
          )}>
            {color.name}
          </p>
          <p className={cn(
            'text-muted-foreground',
            size === 'sm' ? 'text-[8px]' : 'text-[10px]'
          )}>
            {color.code}
          </p>
        </div>
      )}
    </button>
  );
}
